/**
 * Histórico tab — version timeline + diff viewer + restore.
 *
 * Lê /api/ia/sdr-conversation/versions (list) + /:versionId (full snapshot)
 * + POST /restore. Diff client-side simples linha-a-linha contra o markdown
 * derivado (systemPromptDerived) — mesma fonte que o preview do builder usa.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  listSdrConversationVersions,
  getSdrConversationVersion,
  restoreSdrConversationVersion,
} from '../../services/iaConsoleSdr';
import {
  SdrConversationVersionsList,
  SdrConversationVersion,
} from '../../types';

interface HistoricoTabProps {
  /** Role do usuário — restaurar só pra owner/admin/gerente/super_admin. */
  canRestore: boolean;
  /** Quando o restore termina, parent recarrega a aba Atendente IA. */
  onRestoreApplied?: () => void;
}

interface DiffLine {
  kind: 'unchanged' | 'add' | 'del';
  text: string;
}

/** Diff linha-a-linha trivial. Suficiente pra mostrar mudanças entre versões. */
function lineDiff(oldText: string, newText: string): DiffLine[] {
  const oldLines = oldText.split('\n');
  const newLines = newText.split('\n');
  const oldSet = new Set(oldLines);
  const newSet = new Set(newLines);

  const result: DiffLine[] = [];
  const max = Math.max(oldLines.length, newLines.length);
  for (let i = 0; i < max; i++) {
    const o = oldLines[i];
    const n = newLines[i];
    if (o === n) {
      if (o !== undefined) result.push({ kind: 'unchanged', text: o });
    } else if (o === undefined) {
      result.push({ kind: 'add', text: n });
    } else if (n === undefined) {
      result.push({ kind: 'del', text: o });
    } else {
      // Distintas: marca remove old + add new
      if (!newSet.has(o)) result.push({ kind: 'del', text: o });
      if (!oldSet.has(n)) result.push({ kind: 'add', text: n });
    }
  }
  return result;
}

const HistoricoTab: React.FC<HistoricoTabProps> = ({ canRestore, onRestoreApplied }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [list, setList] = useState<SdrConversationVersionsList | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [snapshotMain, setSnapshotMain] = useState<SdrConversationVersion | null>(null);
  const [compareId, setCompareId] = useState<string | null>(null);
  const [snapshotCompare, setSnapshotCompare] = useState<SdrConversationVersion | null>(null);
  const [restoreInProgress, setRestoreInProgress] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [restoreSuccess, setRestoreSuccess] = useState<string | null>(null);

  // Initial load + auto-select most recent
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listSdrConversationVersions()
      .then((data) => {
        if (cancelled) return;
        setList(data);
        // Auto-select most recent (não-current se houver, senão o current)
        const first = data.versions[0];
        if (first) setSelectedId(first.versionId);
        // Auto-pick compare = segunda mais recente
        const second = data.versions[1];
        if (second) setCompareId(second.versionId);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Erro ao carregar histórico de versões');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  // Load main snapshot when selectedId changes
  useEffect(() => {
    if (!selectedId) {
      setSnapshotMain(null);
      return;
    }
    let cancelled = false;
    getSdrConversationVersion(selectedId)
      .then((s) => !cancelled && setSnapshotMain(s))
      .catch((err) => !cancelled && setError(err?.message || 'Erro ao carregar versão'));
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  // Load compare snapshot
  useEffect(() => {
    if (!compareId) {
      setSnapshotCompare(null);
      return;
    }
    let cancelled = false;
    getSdrConversationVersion(compareId)
      .then((s) => !cancelled && setSnapshotCompare(s))
      .catch(() => {
        // Silencioso: comparação não-bloqueante
      });
    return () => {
      cancelled = true;
    };
  }, [compareId]);

  const diff = useMemo(() => {
    if (!snapshotMain || !snapshotCompare) return null;
    return lineDiff(snapshotCompare.systemPromptDerived, snapshotMain.systemPromptDerived);
  }, [snapshotMain, snapshotCompare]);

  const handleRestore = async () => {
    if (!selectedId || !canRestore) return;
    if (!window.confirm('Restaurar essa versão como atual? Uma nova versão será criada com este conteúdo.')) {
      return;
    }
    setRestoreInProgress(true);
    setRestoreError(null);
    setRestoreSuccess(null);
    try {
      const res = await restoreSdrConversationVersion(selectedId);
      setRestoreSuccess(
        `Restaurado da versão ${res.restoredFrom.version} → versão atual ${res.current.version}`,
      );
      // Recarrega lista (ganhou +1 versão)
      const fresh = await listSdrConversationVersions();
      setList(fresh);
      if (onRestoreApplied) onRestoreApplied();
    } catch (err: any) {
      setRestoreError(err?.message || 'Erro ao restaurar versão');
    } finally {
      setRestoreInProgress(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: 'var(--muted, #6b7280)' }}>
        Carregando histórico…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 32, color: '#b91c1c', background: '#fee2e2', borderRadius: 8 }}>
        Erro: {error}
      </div>
    );
  }

  if (!list || list.versions.length === 0) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: 'var(--muted, #6b7280)' }}>
        Nenhuma versão arquivada ainda. Salve no Atendente IA para criar a primeira versão.
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 16, padding: 18 }}>
      {/* Timeline */}
      <aside style={{
        border: '1px solid var(--border, #e5e7eb)',
        borderRadius: 12,
        background: '#fff',
        overflow: 'hidden',
        height: 'fit-content',
      }}>
        <header style={{
          padding: '10px 14px',
          borderBottom: '1px solid var(--border, #e5e7eb)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <strong style={{ fontSize: 13 }}>Versões</strong>
          <span style={{
            background: '#eef2ff',
            color: '#4338ca',
            fontSize: 11,
            padding: '2px 8px',
            borderRadius: 12,
            fontWeight: 600,
          }}>
            {list.versions.length}
          </span>
        </header>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {list.versions.map((v) => {
            const isSelected = v.versionId === selectedId;
            const isCompare = v.versionId === compareId;
            const isCurrent = v.version === list.currentVersion;
            return (
              <li
                key={v.versionId}
                style={{
                  padding: '12px 14px',
                  borderTop: '1px solid var(--border, #e5e7eb)',
                  background: isSelected ? '#eef2ff' : 'transparent',
                  borderLeft: isSelected
                    ? '3px solid #4338ca'
                    : isCompare
                    ? '3px solid #a855f7'
                    : '3px solid transparent',
                  cursor: 'pointer',
                }}
                onClick={() => setSelectedId(v.versionId)}
              >
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, fontWeight: 600 }}>
                    {v.version}
                  </code>
                  {isCurrent && (
                    <span style={{
                      fontSize: 10,
                      background: '#dcfce7',
                      color: '#166534',
                      padding: '1px 6px',
                      borderRadius: 8,
                    }}>
                      atual
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--muted, #6b7280)', marginTop: 4 }}>
                  {new Date(v.createdAt).toLocaleString('pt-BR')}
                  {v.createdBy ? ` · ${v.createdBy}` : ''}
                </div>
                <div style={{ fontSize: 11.5, color: '#4b5563', marginTop: 4 }}>
                  {v.blocksCount} blocos · {v.totalBytes} bytes
                </div>
                {v.note && (
                  <div style={{
                    fontSize: 11,
                    color: '#4338ca',
                    marginTop: 4,
                    fontStyle: 'italic',
                    lineHeight: 1.4,
                  }}>
                    {v.note}
                  </div>
                )}
                {!isSelected && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCompareId(v.versionId);
                    }}
                    style={{
                      marginTop: 6,
                      fontSize: 10.5,
                      padding: '2px 7px',
                      background: isCompare ? '#a855f7' : '#f3f4f6',
                      color: isCompare ? '#fff' : '#374151',
                      border: 'none',
                      borderRadius: 6,
                      cursor: 'pointer',
                    }}
                  >
                    {isCompare ? '✓ comparando' : 'comparar'}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </aside>

      {/* Detail */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <header style={{
          padding: '14px 16px',
          border: '1px solid var(--border, #e5e7eb)',
          borderRadius: 12,
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>
              {snapshotMain ? (
                <>
                  Comparando{' '}
                  <code style={{ color: '#a855f7', fontFamily: 'JetBrains Mono, monospace' }}>
                    {snapshotCompare?.version || '—'}
                  </code>{' '}
                  →{' '}
                  <code style={{ color: '#4338ca', fontFamily: 'JetBrains Mono, monospace' }}>
                    {snapshotMain.version}
                  </code>
                </>
              ) : (
                'Selecione uma versão na timeline'
              )}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--muted, #6b7280)' }}>
              Diff client-side sobre o markdown derivado.
            </div>
          </div>
          {canRestore && snapshotMain && (
            <button
              type="button"
              onClick={handleRestore}
              disabled={restoreInProgress}
              style={{
                padding: '8px 16px',
                background: '#4338ca',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                cursor: restoreInProgress ? 'wait' : 'pointer',
                fontSize: 13,
                fontWeight: 600,
                opacity: restoreInProgress ? 0.7 : 1,
              }}
            >
              {restoreInProgress ? 'Restaurando…' : `Restaurar ${snapshotMain.version}`}
            </button>
          )}
        </header>

        {restoreSuccess && (
          <div style={{
            padding: '10px 14px',
            background: '#dcfce7',
            color: '#166534',
            border: '1px solid #86efac',
            borderRadius: 8,
            fontSize: 13,
          }}>
            ✓ {restoreSuccess}
          </div>
        )}
        {restoreError && (
          <div style={{
            padding: '10px 14px',
            background: '#fee2e2',
            color: '#b91c1c',
            border: '1px solid #fca5a5',
            borderRadius: 8,
            fontSize: 13,
          }}>
            ✗ {restoreError}
          </div>
        )}

        {snapshotMain && (
          <div style={{
            border: '1px solid var(--border, #e5e7eb)',
            borderRadius: 12,
            background: '#fff',
            overflow: 'hidden',
          }}>
            <div style={{
              padding: '10px 14px',
              background: '#f9fafb',
              borderBottom: '1px solid var(--border, #e5e7eb)',
              fontSize: 12,
              fontWeight: 600,
            }}>
              {snapshotCompare ? 'Diff de markdown derivado' : 'Markdown derivado'}
            </div>
            <pre style={{
              margin: 0,
              padding: 14,
              fontSize: 12,
              lineHeight: 1.6,
              fontFamily: 'JetBrains Mono, monospace',
              maxHeight: 600,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
              {diff
                ? diff.map((line, i) => {
                    const bg =
                      line.kind === 'add'
                        ? 'rgba(16,185,129,.10)'
                        : line.kind === 'del'
                        ? 'rgba(244,63,94,.10)'
                        : 'transparent';
                    const mark = line.kind === 'add' ? '+' : line.kind === 'del' ? '−' : ' ';
                    const markColor =
                      line.kind === 'add'
                        ? '#059669'
                        : line.kind === 'del'
                        ? '#dc2626'
                        : '#9ca3af';
                    return (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          gap: 8,
                          background: bg,
                          padding: '1px 6px',
                          margin: '0 -6px',
                          borderRadius: 3,
                        }}
                      >
                        <span style={{ width: 14, color: markColor, fontWeight: 700, textAlign: 'center' }}>
                          {mark}
                        </span>
                        <span style={{ flex: 1 }}>{line.text || ' '}</span>
                      </div>
                    );
                  })
                : snapshotMain.systemPromptDerived}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoricoTab;
